import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { BaseComponent, CommunitiesService, FSP_COMMUNITY_STRING_ID, UserService, environment } from "@mypxplat/xplat/core";
import { WebCommunityService } from "@mypxplat/xplat/web/core";
import { takeUntil } from "rxjs/operators";
import { CommunityFeedRow, CommunityLookup, buildCommunityFeedRow } from "./community-feed.util";

/**
 * "Latest from your communities" — the newest parent posts across the communities the
 * signed-in user follows.
 *
 * Renders the whole section, heading included, and nothing at all when there's nothing to
 * show: a reader who follows no communities (or whose communities have been quiet) gets no
 * empty box on their home page. The /communities right rail keeps its own richer version —
 * day-grouped, with a follow-nothing call to action — and shares this one's row projection
 * (see community-feed.util) so the two read identically.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-feed",
  templateUrl: "community-feed.component.html",
  standalone: false,
})
export class CommunityFeedComponent extends BaseComponent implements OnInit {
  /** Most rows to show. */
  @Input() limit: number = 8;

  /**
   * With `multiCommunity` off there is only ever one community, so naming it reads better
   * than "your communities" — and /communities isn't routable in that build either (the nav
   * links straight to /community, see fender-nav). Both follow the flag.
   *
   * The heading is refined to the community's configured title once the list loads, so a
   * rename in config carries through; this literal is only the pre-load value.
   */
  public headingText: string = environment.features?.multiCommunity ? "Latest from your communities" : "Latest from Fender Studio Pro Community";
  public viewAllLink: string = environment.features?.multiCommunity ? "/communities" : "/community";

  public rows: CommunityFeedRow[] = [];
  public loading: boolean = true;

  private _communityLookup: CommunityLookup = {};
  /** The community list has arrived (or failed), so `_communityLookup` is what the viewer can see. */
  private _communitiesKnown = false;
  private _followed: string[] = [];

  constructor(
    userService: UserService,
    public communitiesService: CommunitiesService,
    public webCommunityService: WebCommunityService,
    private _router: Router
  ) {
    super(userService);
  }

  ngOnInit() {
    // Titles and accents: posts carry only `community_string_id`.
    this.communitiesService.communities$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
      if (!list) return;
      const lookup: CommunityLookup = {};
      for (const community of list) {
        if (community?.string_id) lookup[community.string_id] = { title: community.title, accentColor: community.branding?.accent_color };
      }
      this._communityLookup = lookup;
      this._communitiesKnown = true;
      if (!environment.features?.multiCommunity) {
        const fsp = lookup[FSP_COMMUNITY_STRING_ID];
        if (fsp?.title) this.headingText = `Latest from ${fsp.title}`;
      }
      this._load();
    });
    // A failed load never emits: settle on what's known (nothing) rather than spin forever.
    Promise.resolve(this.communitiesService.load()).then((list) => {
      if (list?.length || this._communitiesKnown) return;
      this._communitiesKnown = true;
      this._load();
    });

    // Follow state lives on `user_profiles/{userId}.followingCommunities`. Read it from the
    // emitted profile rather than `webCommunityService.profile` — on a refresh AppService
    // hydrates the subject from localStorage without setting that property.
    this.webCommunityService.firebaseProfile$.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
      const following = profile?.followingCommunities || {};
      const followed = this.scopeToEnabledCommunities(Object.keys(following).filter((key) => !!following[key]));
      // Same set as last time — nothing to refetch.
      if (followed.length === this._followed.length && followed.every((id) => this._followed.indexOf(id) > -1)) return;
      this._followed = followed;
      this._load();
    });
    if (this.userService.user) this.webCommunityService.getFirebaseProfile();
  }

  /**
   * With `multiCommunity` off, FSP is the only community the app exposes: /communities does
   * not route and non-FSP `/community/:id` paths are refused by multiCommunityCanMatch. A
   * follow map can still name other communities though — set on another surface, or left over
   * from testing — so without this the section would surface posts the reader has no way to
   * open, under a heading naming FSP.
   */
  private scopeToEnabledCommunities(followed: string[]): string[] {
    if (environment.features?.multiCommunity) return followed;
    return followed.filter((stringId) => stringId === FSP_COMMUNITY_STRING_ID);
  }

  /** Opens a post, leaving a trail back to wherever this section is embedded. */
  public openRow(row: CommunityFeedRow) {
    // `<myp-community-post-detail>`'s back button falls back to the FSP community without
    // this; the empty collections are what its state-restore path expects to iterate.
    this.webCommunityService.communityListBackCache = {
      key: "communities-index",
      activeParentPosts: [],
      activeParentThreadMap: {},
      lastVisiblePost: null,
      activePostsLimitReached: false,
      returnUrl: this._router.url,
    };
    // `detailPath` has no leading slash — add one so Angular routes from the root.
    this._router.navigateByUrl("/" + row.detailPath);
  }

  private _load() {
    // Only followed communities this viewer can see — the follow map can name others: an
    // `employee_only` community a non-employee once joined, one served only elsewhere. So wait
    // for the community list before choosing.
    if (!this._communitiesKnown) return;
    const followed = this._followed.filter((id) => !!this._communityLookup[id]);
    if (!followed.length) {
      this.rows = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    this.webCommunityService
      .getRecentPostsForCommunities(followed, this.limit)
      .then((posts) => {
        this.rows = (posts || []).slice(0, this.limit).map((post) => buildCommunityFeedRow(post, this._communityLookup));
        this.loading = false;
      })
      .catch(() => {
        this.rows = [];
        this.loading = false;
      });
  }
}
